/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import {
  employeePayloadModel,
  employeeResponseModel,
  UploadedImageDto
} from '../api/EmployeesAPI.model'
import EmpoloyeesAPI from '../api/EmployeesAPI'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

const EMPLOYEE_QUERY_KEY = 'employees'

/**
 * Deliberately takes no employee argument.
 *
 * It used to take the id of the row being edited and close over it inside
 * `updateEmployee`'s `mutationFn`. react-query re-points a mutation at the
 * newest options on every render and reads `mutationFn` lazily when the
 * request actually runs, so the id that went out was "whatever the page holds
 * now" rather than "the row the user pressed Save on", and the page clears
 * that state as part of saving, which is how `PUT /users/undefined` was built.
 * The id travels in the mutation variables instead, where nothing can move it.
 */
export function useEmployeesQuery() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const {
    data: employeesList,
    isLoading: employeesListLoading,
    refetch: refetchEmployees
  } = useQuery({
    queryKey: [EMPLOYEE_QUERY_KEY],
    queryFn: () => EmpoloyeesAPI.getEmployees()
  })

  const { mutate: registerEmployee, mutateAsync: registerEmployeeAsync } =
    useMutation<void, ErrorModel, employeePayloadModel>({
      mutationFn: (payload: employeePayloadModel) =>
        EmpoloyeesAPI.registerEmployee(payload),
      onSuccess: () => {
        showSnackbar(
          t('ALERTS.REGISTER_EMPLOYEE_SUCCESS.TITLE'),
          t('ALERTS.REGISTER_EMPLOYEE_SUCCESS.DESCRIPTION'),
          'success'
        )
        queryClient.invalidateQueries({ queryKey: [EMPLOYEE_QUERY_KEY] })
      },
      onError: () => {
        showSnackbar(
          t('ALERTS.REGISTER_EMPLOYEE_ERROR.TITLE'),
          t('ALERTS.REGISTER_EMPLOYEE_ERROR.DESCRIPTION'),
          'error'
        )
      }
    })

  const { mutate: updateEmployee } = useMutation<
    employeeResponseModel,
    ErrorModel,
    { userId: string } & Partial<employeePayloadModel>
  >({
    mutationFn: ({ userId, ...payload }) =>
      EmpoloyeesAPI.updateEmployee(userId, payload),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.UPDATE_EMPLOYEE_SUCCESS.TITLE'),
        t('ALERTS.UPDATE_EMPLOYEE_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_QUERY_KEY] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.UPDATE_EMPLOYEE_ERROR.TITLE'),
        t('ALERTS.UPDATE_EMPLOYEE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteEmployee } = useMutation<void, ErrorModel, string>({
    mutationFn: (userId: string) => EmpoloyeesAPI.deleteEmployee(userId),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_EMPLOYEE_SUCCESS.TITLE'),
        t('ALERTS.DELETE_EMPLOYEE_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_QUERY_KEY] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_EMPLOYEE_ERROR.TITLE'),
        t('ALERTS.DELETE_EMPLOYEE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: uploadAvatar } = useMutation<
    UploadedImageDto,
    ErrorModel,
    File
  >({
    mutationFn: (file: File) => EmpoloyeesAPI.uploadAvatar(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_QUERY_KEY] })
    }
  })

  return {
    employeesList,
    employeesListLoading,
    refetchEmployees,
    registerEmployee,
    registerEmployeeAsync,
    updateEmployee,
    deleteEmployee,
    uploadAvatar
  }
}
